import { useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import { clearSwrCache, clearSwrCacheByPrefix } from '../lib/swrCache';
import type { Venue } from '../types';

/**
 * UI-level moderation status. Maps to the underlying `(active, reviewStatus)`
 * pair when saving:
 *  - `active`    → `active=true,  reviewStatus='approved'`  (visible publicly)
 *  - `dismissed` → `active=false, reviewStatus='dismissed'` (venue hidden, events still shown)
 *  - `declined`  → `active=false, reviewStatus='declined'`  (venue + events hidden)
 */
export type VenueStatus = 'active' | 'dismissed' | 'declined';

export function venueToStatus(v: Pick<Venue, 'active' | 'reviewStatus'>): VenueStatus {
  if (v.active) return 'active';
  if (v.reviewStatus === 'declined') return 'declined';
  return 'dismissed';
}

const STATUS_PATCH: Record<VenueStatus, { active: boolean; reviewStatus: 'approved' | 'dismissed' | 'declined' }> = {
  active: { active: true, reviewStatus: 'approved' },
  dismissed: { active: false, reviewStatus: 'dismissed' },
  declined: { active: false, reviewStatus: 'declined' },
};

interface Props {
  venue: Venue;
  /** Called with the updated venue after a successful save. */
  onSaved?: (v: Venue) => void;
  /** Compact = `form-select-sm`. Default = standard size. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Inline moderation-status dropdown for admins. PATCHes the venue immediately
 * on change and refreshes the public-venue + events SWR caches so other open
 * tabs/pages pick up the new state on next load.
 */
export default function VenueStatusSelect({ venue, onSaved, size = 'sm', className = '' }: Props) {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<VenueStatus>(() => venueToStatus(venue));
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (next: VenueStatus) => {
    if (next === status || saving) return;
    const prev = status;
    setStatus(next);
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patch<Venue>(
        `/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`,
        STATUS_PATCH[next],
      );
      clearSwrCache('venues:public');
      clearSwrCacheByPrefix('events:');
      onSaved?.(updated);
    } catch (e) {
      setStatus(prev);
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const selectClass = `form-select ${size === 'sm' ? 'form-select-sm' : ''} mh-venue-status-select`;

  return (
    <span className={`d-inline-flex align-items-center gap-1 ${className}`.trim()}>
      <label htmlFor={`venue-status-${venue.rowKey}`} className="visually-hidden">
        {`Status for ${venue.name}`}
      </label>
      <select
        id={`venue-status-${venue.rowKey}`}
        className={selectClass}
        value={status}
        onChange={(e) => void handleChange(e.target.value as VenueStatus)}
        disabled={saving}
        title="Active = public · Dismissed = hide venue, keep events · Declined = hide both"
        aria-label={`Status for ${venue.name}`}
      >
        <option value="active">Active</option>
        <option value="dismissed">Inactive (hidden)</option>
        <option value="declined">Declined (hide events)</option>
      </select>
      {error && (
        <span className="small text-danger" role="alert" title={error}>
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" /> error
        </span>
      )}
    </span>
  );
}
